/**
 * Pure helpers for the "Add property" wizard on the Properties page.
 *
 * The wizard adds an already-owned property by hand (no Find/pipeline origin),
 * so it must synthesize the same multi-table shape the Closed-Won flow writes:
 *   - a `property_data` JSONB blob in the `MockProperty` shape (every downstream
 *     screen reads identity from here, not from typed columns),
 *   - a `property_setting` strategy loan blob (via `splitStrategySettings`),
 *   - a `loans` row whose `loan_type` selects the active financing slot.
 *
 * Everything here is deterministic and free of React/Supabase deps so it can be
 * unit-tested in isolation. The mutation that writes these lives in
 * `@/hooks/use-add-property`.
 */
import type { MockProperty } from "@/data/mockProperties";
import type { LoanType } from "@/hooks/use-loans";
import type { StrategyPatch } from "@/lib/property-setting";
import type {
  BrrrrSettings,
  CashflowOverrides,
  FlipSettings,
  LoanInputs,
  Strategy,
} from "@/types/pipeline";

/** Supabase Storage bucket holding property photos, one folder per property. */
export const PROPERTY_IMAGES_BUCKET = "property-images";

/**
 * Object path for a property's cover photo: `{pipeline_property_id}/cover-<ts>.<ext>`.
 * The folder is the property id so reads can list/fetch by it, and the storage
 * RLS policy checks folder[1] against the owning workspace. `now` is passed in
 * (not read from `Date`) so the helper stays pure/testable.
 */
export function propertyImagePath(
  pipelinePropertyId: string,
  fileName: string,
  now: number,
): string {
  const parts = fileName.split(".");
  const raw = parts.length > 1 ? (parts.pop() ?? "") : "";
  const ext = raw.toLowerCase().replace(/[^a-z0-9]/g, "");
  return `${pipelinePropertyId}/cover-${now}.${ext || "jpg"}`;
}

/** Status the wizard lets the user pick — the property is always owned. */
export type AddPropertyStatus = "owned" | "in-rehab";

/** Which BRRRR loan slot the entered financing describes. */
export type BrrrrLoanKind = "hard_money" | "long_term";

/** Flat form model collected across the wizard's three steps. */
export interface AddPropertyForm {
  // Step 1 — property
  addr1: string;
  city: string;
  state: string;
  zip: string;
  bd: number;
  ba: number;
  sqft: number;
  strategy: Strategy;
  status: AddPropertyStatus;
  /** "" or "YYYY-MM-DD". */
  purchaseDate: string;
  /** "" or an image URL. */
  imgUrl: string;
  // Step 2 — financing
  purchase: number;
  /** Loan amount in dollars — converted to an LTV against the right base. */
  loanValue: number;
  rate: number;
  /** Canonical term length in months. */
  termMonths: number;
  termUnit: "months" | "years";
  points: number;
  /** "" or "YYYY-MM-DD" — the loan's `loan_date` (first payment / origination). */
  emiDate: string;
  /** Only meaningful when `strategy === "BRRRR"`. */
  brrrrLoanKind: BrrrrLoanKind;
  /**
   * Estimated after-repair value. Collected for every strategy — it drives the
   * BRRRR refi LTV, Flip profit/margin, and the Rental equity read.
   */
  arv: number;
  // Step 3 — income
  monthlyRent: number;
  rehabCost: number;
  /** Selling cost as a percent of ARV. Only collected for Flip. */
  sellPct: number;
}

/**
 * Seed holding costs for a hand-added Flip. The wizard does not ask for carry
 * lines, but `flip_holding_cost` must never persist as `{}` — downstream tiles
 * read `hold_months` / `taxes_monthly` / `utilities_monthly` and an absent key
 * is indistinguishable from "not yet entered". Explicit zeros with a 6-month
 * default hold give the Flip panel a complete, editable starting point.
 */
export const FLIP_HOLDING_SEED = {
  hold_months: 6,
  taxes_monthly: 0,
  utilities_monthly: 0,
} satisfies FlipSettings;

/** Same for BRRRR, plus the monthly-cash-flow-override switch (off, no delay). */
export const BRRRR_HOLDING_SEED = {
  hold_months: 6,
  mcf_enabled: false,
  mcf_delay: 0,
  taxes_monthly: 0,
  utilities_monthly: 0,
} satisfies BrrrrSettings;

/**
 * `monthly_cash_flow` seed — every operating line at $0.
 *
 * A hand-added property has no underwriting history, so the wizard persists
 * explicit zeros rather than `{}`. Consumers treat an empty object as "nothing
 * entered" and, once ANY line is filled, switch the remaining lines to
 * rent/purchase heuristics (`property-metrics.ts` `hasOverrides`). Writing every
 * key up front keeps the lines the user never touched at $0 instead of silently
 * fabricating reserves later.
 *
 * `mortgage` is deliberately absent: it is derived from the loan, and a stored
 * `0` would win over the computed debt service wherever
 * `overrides.mortgage ?? monthlyDebt` is read.
 */
export const ZERO_CASHFLOW = {
  gross: 0,
  other: 0,
  vacancy: 0,
  mgmt: 0,
  taxes: 0,
  insurance: 0,
  maint: 0,
  capex: 0,
  hoa: 0,
  utilities: 0,
} satisfies CashflowOverrides;

/**
 * Seed rate/points/term for a strategy's acquisition loan — mirrors
 * `SLOT_DEFAULTS` in `@/lib/closed-won`. BRRRR depends on the chosen slot:
 * hard money is a short months-term loan, the refinance is a long years-term.
 */
export function strategyLoanDefaults(
  strategy: Strategy,
  brrrrKind: BrrrrLoanKind,
): { rate: number; points: number; termMonths: number; termUnit: "months" | "years" } {
  if (strategy === "Rental") {
    return { rate: 7, points: 1, termMonths: 360, termUnit: "years" };
  }
  if (strategy === "Flip") {
    return { rate: 11, points: 2, termMonths: 12, termUnit: "months" };
  }
  // BRRRR
  return brrrrKind === "long_term"
    ? { rate: 7, points: 1, termMonths: 360, termUnit: "years" }
    : { rate: 11, points: 2, termMonths: 12, termUnit: "months" };
}

/**
 * The dollar base an entered loan value is an LTV of. Acquisition loans
 * (Rental long-term, Flip/BRRRR hard money) are a percent of purchase; the
 * BRRRR refinance is a percent of ARV.
 */
export function loanBase(form: AddPropertyForm): number {
  if (form.strategy === "BRRRR" && form.brrrrLoanKind === "long_term") {
    return Math.max(0, form.arv);
  }
  return Math.max(0, form.purchase);
}

/** Build the `LoanInputs` blob from the form's financing fields. */
export function buildLoanInputs(form: AddPropertyForm): LoanInputs {
  const base = loanBase(form);
  const value = Math.max(0, form.loanValue);
  // Round to 5dp so the dollar value the user typed round-trips back to the
  // same amount (matches LoanEditDrawer / buildClosedWonPatch).
  const ltv = base > 0 ? Math.round((value / base) * 100 * 1e5) / 1e5 : 0;
  const months = Math.max(0, Math.round(form.termMonths));
  const legacyTerm =
    form.termUnit === "years" ? Math.max(1, Math.round(months / 12)) : Math.max(1, months);
  return {
    ltv,
    rate: Math.max(0, form.rate),
    term: legacyTerm,
    term_months: months,
    term_unit: form.termUnit,
    points: Math.max(0, form.points),
    loan_date: form.emiDate || null,
    is_enabled: true,
  };
}

/**
 * Nest the loan into its strategy slot for `splitStrategySettings`. Rental →
 * long-term, Flip → hard money, BRRRR → the chosen slot (hard money or the
 * refinance long-term). `maturity_date` / `term_months` are recomputed by
 * `splitStrategySettings`.
 *
 * Flip and BRRRR also carry their holding-cost seed (and, for Flip, the entered
 * selling-cost percent) so `flip_holding_cost` / `brrrr_holding_cost` /
 * `flip_selling_cost` are written with real values instead of `{}` and `null`.
 * Rental has no holding-cost column.
 *
 * The Flip selling cost is written in both units — the entered percent and the
 * dollars it comes to on the entered ARV — so the new row already carries what
 * the backfill computes for older ones.
 */
export function buildStrategyPatch(form: AddPropertyForm): StrategyPatch {
  const loan = buildLoanInputs(form);
  if (form.strategy === "Rental") return { rental: { long_term: loan } };
  if (form.strategy === "Flip") {
    const sellPct = Math.max(0, form.sellPct);
    return {
      flip: {
        ...FLIP_HOLDING_SEED,
        sell_pct: sellPct,
        sell_value: Math.round(Math.max(0, form.arv) * (sellPct / 100)),
        hard_money: loan,
      },
    };
  }
  return {
    brrrr: {
      ...BRRRR_HOLDING_SEED,
      ...(form.brrrrLoanKind === "long_term"
        ? { long_term: loan }
        : { hard_money: loan }),
    },
  };
}

/**
 * `loans.loan_type` for the active financing slot. Rental holds a conventional
 * loan; Flip and BRRRR-acquisition use hard money; the BRRRR refinance is a
 * refinance loan. This selects the scenario `cashflow-metrics` reads.
 */
export function resolveLoanType(
  strategy: Strategy,
  brrrrKind: BrrrrLoanKind,
): LoanType {
  if (strategy === "Rental") return "conventional";
  if (strategy === "Flip") return "hard_money";
  return brrrrKind === "long_term" ? "refinance" : "hard_money";
}

/**
 * After-repair value to persist. The wizard asks every strategy for an estimate
 * (Flip needs it for profit/margin, BRRRR for the refi LTV, Rental for equity),
 * so it is stored whenever a value was entered.
 */
export function resolveArv(form: AddPropertyForm): number | null {
  return form.arv > 0 ? form.arv : null;
}

/**
 * Synthesize the `property_data` JSONB blob. The Properties/pipeline UIs read
 * identity (address, beds, photo) from here, so every field the `MockProperty`
 * shape declares is populated — unknowns default to 0/false rather than being
 * omitted, so downstream `?? 0` fallbacks and typed reads stay happy.
 */
export function buildPropertyData(
  form: AddPropertyForm,
  propertyId: string,
): MockProperty {
  return {
    id: propertyId,
    address: form.addr1.trim(),
    city: form.city.trim(),
    state: form.state.trim(),
    zip: form.zip.trim(),
    lat: 0,
    lng: 0,
    price: Math.max(0, form.purchase),
    bedrooms: Math.max(0, form.bd),
    bathrooms: Math.max(0, form.ba),
    sqft: Math.max(0, form.sqft),
    yearBuilt: 0,
    propertyType: "sfh",
    // Listing status — kept "active"; the wizard never creates a sold property.
    status: "active",
    daysOnMarket: 0,
    imageUrl: form.imgUrl.trim(),
    estimatedRent: Math.max(0, form.monthlyRent),
    arvEstimate: Math.max(0, form.arv),
    repairEstimate: Math.max(0, form.rehabCost),
    capRate: 0,
    cashOnCash: 0,
    grossRentMultiplier: 0,
    section8Fmr: 0,
    vacancyRate: 0,
    isForeclosure: false,
    isPreForeclosure: false,
    isAuction: false,
    isReo: false,
    isBankOwned: false,
    equityEstimate: 0,
    motivatedSeller: false,
    belowMarketValue: false,
  };
}
